import { Fragment, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { Menu, Transition } from '@headlessui/react';
import parse, { DOMNode, domToReact } from 'html-react-parser';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { NotificationItem, NotificationsList } from '@/interfaces';
import { Dropdown, Icon, Spinner, Typography } from '@/components/common';
import Icons from '@/const/icons';
import {
	GET_NOTIFICATIONS,
	GET_PENDING_NOTIFICATIONS,
	notificationsService,
} from '@/api';
import { EmptyBoxScreen, ErrorIcon } from '@/components/dashboard';
import { useSession } from 'next-auth/react';
import { useEventSource } from '@/hooks/useEventSource';
import { formatErrorMessage, formatRelativeTimeAbbreviated } from '@/lib';
import { useNotification } from '@/hooks';
import { useRouter } from 'next/router';
import Link from 'next/link';
import AppRoutes from '@/const/routes';
import { useHandleError } from '@/hooks/useHandleError';

interface NotificationsProps {
	classNameMenuItems?: string;
}
export const Notifications: React.FC<NotificationsProps> = ({
	classNameMenuItems,
}) => {
	const session = useSession();
	const router = useRouter();
	const { toastError } = useNotification();
	const { getCasesNotificationError, getMarketingNotificationError } =
		useHandleError();
	const token = session.data?.accessToken;
	const { data: sseNotification } = useEventSource({
		url: `${process.env.NEXT_PUBLIC_API}/sse/new-notification/subscribe?token=${token}`,
	});

	const [hasNotifications, setHasNotifications] = useState<boolean>(false);
	const [notificationsList, setNotificationsList] = useState<
		NotificationItem[]
	>([]);
	const [isOpen, setIsOpen] = useState(false);
	const menuRef = useRef(null);
	const [height, setHeight] = useState(0);

	useEffect(() => {
		// Solo se ejecuta en cliente
		if (typeof window !== 'undefined') {
			const handleResize = () => setHeight(window.innerHeight);

			// Set inicial
			handleResize();

			// Event listener
			window.addEventListener('resize', handleResize);

			// Limpieza
			return () => window.removeEventListener('resize', handleResize);
		}
	}, []);

	const { data: countNotifications, refetch: refetchCountNotifications } =
		useQuery({
			queryKey: [GET_PENDING_NOTIFICATIONS, sseNotification],
			queryFn: () => notificationsService.getPending(),
			refetchOnWindowFocus: false,
		});

	const fetchData = async ({
		pageParam = 1,
	}: {
		pageParam?: number;
	}): Promise<NotificationsList> => {
		const response = await notificationsService.getNotifications(pageParam, 10);
		return response.data;
	};

	const {
		data: notificationsPaginated,
		fetchNextPage,
		hasNextPage,
		isLoading: isLoadingList,
		isRefetching: isRefetchList,
		refetch,
	} = useInfiniteQuery({
		initialData: undefined,
		initialPageParam: 1,
		queryKey: [GET_NOTIFICATIONS],
		queryFn: fetchData,
		getNextPageParam(lastPage) {
			return lastPage.page < lastPage.totalPages
				? lastPage.page + 1
				: undefined;
		},
		refetchOnWindowFocus: false,
	});

	useEffect(() => {
		const quantity = countNotifications?.data.totalNotifications;
		if (quantity && quantity > 0) {
			setHasNotifications(true);
		} else {
			setHasNotifications(false);
		}
	}, [countNotifications]);

	useEffect(() => {
		const parsedNotification =
			notificationsPaginated?.pages
				.flatMap((page) => page.items)
				.filter((item) => item.is_visible)
				.map((item) => ({
					...item,
					description: item.description
						.replace('http://localhost:4000', '')
						.replace('https://api-dev.claimmagic.com', ''),
				})) || [];

		setNotificationsList(parsedNotification);
	}, [notificationsPaginated]);

	useEffect(() => {
		const handleClickOutside = (event: any) => {
			if (menuRef.current && !(menuRef.current as any).contains(event.target)) {
				setIsOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	useEffect(() => {
		if (sseNotification) {
			let notification = JSON.parse(sseNotification);
			notification = notification.payload.data;
			const currentNotification = notificationsList.find(
				(n) => n.id === notification.id
			);

			if (currentNotification) {
				const newItem = {
					...currentNotification,
					is_read: false,
				};
				const newNotificationList = notificationsList.filter(
					(n) => n.id !== currentNotification.id
				);

				setNotificationsList([newItem, ...newNotificationList]);
			}
			setHasNotifications(true);
		}
	}, [sseNotification]);

	useEffect(() => {
		const handleResize = () => setHeight(window.innerHeight);
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	const handleScroll = (e: any): void => {
		const { scrollHeight, scrollTop, clientHeight } = e.target;
		const bottom =
			Math.abs(scrollHeight - Math.ceil(scrollTop) - clientHeight) <= 1;
		if (bottom && hasNextPage && !isRefetchList) {
			fetchNextPage();
		}
	};

	const transform = (node: any, id: string): React.ReactElement | undefined => {
		if (node.name === 'a') {
			const href = node.attribs.href;
			const children = node.children;

			return (
				<span
					key={href}
					className="!text-blue-link underline cursor-pointer"
					onClick={async () => {
						setIsOpen(false);
						await handleRemoveNotification(id);
						await router.push(href);
					}}
				>
					{domToReact(children)}
				</span>
			);
		}
	};

	// CLAIM-3486 — the onboarding notification is now email-verification-only
	// (the profile-completion item was removed). Notification copy is
	// backend-authored, so we key off "Verify" in the title rather than the
	// old exact "Verify Your Email and Complete Your Profile" string, and
	// send the user to Settings when the description has no embedded link.
	const isVerificationNotification = (title: string): boolean =>
		title.toLowerCase().includes('verify');

	const isLoading = isLoadingList;

	const handleRemoveNotification = async (id: string) => {
		try {
			await notificationsService.deleteNotification(id);
			refetch();
			refetchCountNotifications();
		} catch (error) {
			toastError(formatErrorMessage(error as any));
		}
	};

	const handleMarkUnread = async (id: string) => {
		try {
			await notificationsService.patchMarkUnread(id);
			refetch();
			refetchCountNotifications();
		} catch (error) {
			toastError(formatErrorMessage(error as any));
		}
	};

	const handleMarkRead = async (id: string) => {
		try {
			await notificationsService.patchMarkRead(id);
			refetch();
			refetchCountNotifications();
		} catch (error) {
			toastError(formatErrorMessage(error as any));
		}
	};

	const casesError = getCasesNotificationError();
	const campaignsError = getMarketingNotificationError();

	return (
		<div className="!w-5 !h-5" ref={menuRef}>
			<Menu as="div" className="relative inline-block text-left z-[10]">
				<>
					<div>
						<Menu.Button
							className={'relative'}
							onClick={() => {
								refetch();
								setIsOpen(!isOpen);
							}}
						>
							<div
								className={clsx(
									'bg-[#FF0043] rounded-full w-2 h-2 absolute right-0.5 top-0 border border-white',
									{
										hidden: !hasNotifications,
									}
								)}
							/>
							<Icon src={Icons.notification} className="!w-5 !h-5" />
						</Menu.Button>
					</div>
					<Transition
						show={isOpen}
						as={Fragment}
						enter="transition ease-out duration-100"
						enterFrom="transform opacity-0 scale-95"
						enterTo="transform opacity-100 scale-100"
						leave="transition ease-in duration-75"
						leaveFrom="transform opacity-100 scale-100"
						leaveTo="transform opacity-0 scale-95"
					>
						<Menu.Items
							className={clsx(
								'absolute right-0 mt-2 w-[21rem] md:w-[28rem] origin-top-right rounded-xl bg-white shadow-lg focus:outline-none',
								classNameMenuItems
							)}
						>
							<div className="">
								<div className="bg-purple-150 rounded-t-xl py-3 px-4">
									<Typography type="semibold-14" className="text-white">
										Notifications
									</Typography>
								</div>
								<div
									onScroll={handleScroll}
									className={clsx(
										'max-h-[500px] md:max-h-[632px] xl:max-h-[580px] 2xl:max-h-[632px] scroll-custom',
										{ '!h-[500px]': height < 800 },
										notificationsList.length > 4
											? ' overflow-y-auto'
											: ' overflow-y-visible'
									)}
								>
									{isLoading && (
										<div className="w-full flex justify-center py-3">
											<Spinner className="w-8 h-8" />
										</div>
									)}

									{casesError?.map(({ id, name, date }) => (
										<CustomError
											key={id}
											title="Payment couldn't be processed"
											linkText="case administration"
											href={`${AppRoutes.LAWYERAPP_CASES}/${id}/investigation`}
											caseName={name}
											created_at={date}
										/>
									))}
									{campaignsError?.map(({ id, name, date }) => (
										<CustomError
											key={id}
											title="One or more posts couldn't be published"
											linkText="campaign management"
											href={`${AppRoutes.LAWYERAPP_MARKETING_CAMPAIGN}/${id}`}
											caseName={name}
											created_at={date}
										/>
									))}
									{!isLoading && notificationsList.length === 0 && (
										<div className="py-3 pb-8">
											<EmptyBoxScreen message="You have no notifications at this moment." />
										</div>
									)}
									{!isLoading &&
										notificationsList.map(
											(
												{ is_read, title, description, id, created_at },
												index
											) => (
												<Menu.Item key={index}>
													<div className="flex justify-between py-4 pl-6 relative border-b border-gray-100">
														<div
															className={clsx(
																'bg-[#FF0043] mr-2 rounded-full w-2 h-2 absolute left-2 top-[1.35rem]',
																{
																	hidden: is_read,
																}
															)}
														/>
														<div>
															<Typography type="semibold-16">
																{title}
															</Typography>
															<p className="font-normal text-opacity-black">
																{parse(
																	description.endsWith('.')
																		? description
																		: `${description}.`,
																	{
																		replace: (domNode: DOMNode) =>
																			transform(domNode, id),
																	}
																)}
																{isVerificationNotification(title) &&
																	!description.includes('<a ') && (
																		<>
																			{' '}
																			<Link
																				href={
																					AppRoutes.CLIENTAPP_SETTINGS_PERSONAL
																				}
																				className="!text-blue-link underline cursor-pointer"
																				onClick={() => setIsOpen(false)}
																			>
																				Go to Settings
																			</Link>
																		</>
																	)}
															</p>
															<Typography
																type="regular-16"
																className="text-opacity-black"
															>
																{created_at &&
																	formatRelativeTimeAbbreviated(created_at)}
															</Typography>
														</div>
														{!isVerificationNotification(title) && (
															<Dropdown
																buttonContent={
																	<Icon
																		src={Icons.dotsMenuVertical}
																		className="w-4 h-4 flex-shrink text-[#51526C]"
																		width={16}
																		height={16}
																	/>
																}
																customTypography="regular-14"
																showChevronDownIcon={false}
																classNameMenuItems="w-[170px] bg-white rounded-lg"
																items={[
																	{
																		label: is_read
																			? 'Mark as Unread'
																			: 'Mark as Read',
																		name: 'unread',
																		icon: Icons.unread,
																		onClick: () =>
																			is_read
																				? handleMarkUnread(id)
																				: handleMarkRead(id),
																	},
																	{
																		label: 'Delete',
																		name: 'delete',
																		icon: Icons.trash,
																		onClick: () => handleRemoveNotification(id),
																		classNameItemLabel: 'text-main-red',
																		classNameItemIcon: '!text-main-red',
																	},
																]}
															/>
														)}
													</div>
												</Menu.Item>
											)
										)}
								</div>
							</div>
						</Menu.Items>
					</Transition>
				</>
			</Menu>
		</div>
	);
};

const CustomError: React.FC<{
	title: string;
	caseName: string;
	href: string;
	linkText: string;
	created_at: string;
}> = ({ title, caseName, href, linkText, created_at }) => {
	return (
		<div className="flex justify-between py-4 pl-6 relative border-b border-gray-100">
			<div
				className={clsx(
					'bg-[#FF0043] mr-2 rounded-full w-2 h-2 absolute left-2 top-[1.35rem]'
				)}
			/>
			<div>
				<div className="flex items-center gap-1">
					<ErrorIcon tooltipText="" />
					<Typography type="semibold-16" className="text-main-red">
						{title}
					</Typography>
				</div>
				<div className="flex items-center gap-1">
					<Typography type="bold-12" className="!text-sm text-gray-35">
						Case:
					</Typography>
					<Typography type="regular-14" className="!text-sm text-gray-35">
						{caseName}
					</Typography>
				</div>
				<Typography type="regular-16" className="text-gray-35 mt-1">
					Please go to{' '}
					<Link href={href} className="text-blue-link underline">
						{linkText}
					</Link>{' '}
					and try again.
				</Typography>
				<Typography type="regular-16" className="text-opacity-black">
					{created_at && formatRelativeTimeAbbreviated(created_at)}
				</Typography>
			</div>
		</div>
	);
};
